import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import TextField from '@mui/material/TextField';
import { Button } from '../../../../../components/ui/button';
import {
	Box,
	Checkbox,
	FormControlLabel,
	Grid,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	CircularProgress,
	Autocomplete,
	Alert,
	AlertTitle,
} from '@mui/material';
import * as Yup from 'yup';
import styles from './CreatePartnerSettings.module.scss';
import { CommonPartner } from 'types';
import {
	getCommonPartners,
	createPartner,
	getPartnerTypes,
	PartnerType,
	createPartnerListWorkplace,
} from 'core/API/partners';
import { GetAllWorkplaceList } from 'core/API/workplaces';
import { useGoogleAuth } from './useGoogleAuth';
import { useOneDriveAuth } from './useOneDriveAuth';

interface CreatePartnerSettingsProps {
	backButtonClick: () => void;
	onUserCreated?: () => void;
}

interface Workplace {
	id: number;
	name: string;
	level: number;
	selected: boolean;
	'@id'?: string;
}

const CreatePartnerSettings: React.FC<CreatePartnerSettingsProps> = ({
	backButtonClick,
	onUserCreated,
}) => {
	const [apiError, setApiError] = useState<string | null>(null);
	const [apiSuccess, setApiSuccess] = useState<string | null>(null);
	const [activateDialogOpen, setActivateDialogOpen] = useState(false);
	const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
	const [workplaceUnassignDialogOpen, setWorkplaceUnassignDialogOpen] = useState(false);
	const [pendingWorkplaceId, setPendingWorkplaceId] = useState<number | null>(null);
	const [workplaces, setWorkplaces] = useState<Workplace[]>([]);
	const [loadingWorkplaces, setLoadingWorkplaces] = useState(false);
	const [commonPartners, setCommonPartners] = useState<CommonPartner[]>([]);
	const [loadingCommonPartners, setLoadingCommonPartners] = useState(false);
	const [partnerTypes, setPartnerTypes] = useState<PartnerType[]>([]);
	const [loadingPartnerTypes, setLoadingPartnerTypes] = useState(false);
	const [parameterValues, setParameterValues] = useState<Record<string, string>>({});

	// Google Drive OAuth integration
	const {
		accessToken,
		encryptedRefreshToken: googleEncryptedRefreshToken,
		sharedDrives,
		selectedDriveId,
		isConnected: isGoogleConnected,
		isLoading: isGoogleAuthLoading,
		error: googleAuthError,
		initiateGoogleAuth,
		resetAuth: resetGoogleAuth,
		setSelectedDriveId,
	} = useGoogleAuth();

	// Microsoft OneDrive OAuth integration
	const {
		accessToken: oneDriveAccessToken,
		refreshToken: oneDriveRefreshToken,
		isConnected: isOneDriveConnected,
		isLoading: isOneDriveAuthLoading,
		error: oneDriveAuthError,
		initiateOneDriveAuth,
		resetAuth: resetOneDriveAuth,
	} = useOneDriveAuth();

	// Fetch common partners, partner types, and workplaces on mount
	useEffect(() => {
		const fetchData = async () => {
			try {
				setLoadingCommonPartners(true);
				setLoadingPartnerTypes(true);
				setLoadingWorkplaces(true);

				const [partners, types, workplacesData] = await Promise.all([
					getCommonPartners(),
					getPartnerTypes(),
					GetAllWorkplaceList(),
				]);
				console.log('workplaces', workplacesData);
				setCommonPartners(
					partners.filter((p) => p.available && p.label?.toLowerCase().includes('google drive'))
				);
				setPartnerTypes(types);
				setWorkplaces(
					(Object.values(workplacesData) || []).map((w) => ({
						id: w.id,
						name: w.name,
						level: 1, // You can enhance this with hierarchy logic if needed
						selected: false,
						'@id': `/api/workplaces/${w.id}`,
					}))
				);
			} catch (error: any) {
				console.error('Failed to fetch data:', error);
				setApiError('Failed to load options');
			} finally {
				setLoadingCommonPartners(false);
				setLoadingPartnerTypes(false);
				setLoadingWorkplaces(false);
			}
		};
		fetchData();
	}, []);

	// Auto-fill TOKEN and SHARED_DRIVE_ID parameters when OAuth completes
	useEffect(() => {
		if (accessToken && isGoogleConnected) {
			setParameterValues((prev) => {
				const updated = { ...prev };

				// Find TOKEN / ACCESS_TOKEN parameter (case-insensitive)
				// Use encrypted_refresh_token as the value sent in the access_token field
				const tokenKey = Object.keys(updated).find(
					(key) => key.toLowerCase() === 'token' || key.toLowerCase() === 'access_token'
				);
				if (tokenKey) {
					updated[tokenKey] = googleEncryptedRefreshToken || accessToken;
					console.log('[CreatePartner] Auto-filled TOKEN parameter with encrypted_refresh_token');
				}

				// Find SHARED_DRIVE_ID parameter (case-insensitive)
				if (selectedDriveId) {
					const driveIdKey = Object.keys(updated).find(
						(key) => key.toLowerCase() === 'shared_drive_id' || key.toLowerCase() === 'drive_id'
					);
					if (driveIdKey) {
						updated[driveIdKey] = selectedDriveId;
						console.log('[CreatePartner] Auto-filled SHARED_DRIVE_ID parameter');
					}
				}

				return updated;
			});
		}
	}, [accessToken, googleEncryptedRefreshToken, selectedDriveId, isGoogleConnected]);

	// Auto-fill TOKEN and REFRESH_TOKEN parameters when OneDrive OAuth completes
	useEffect(() => {
		if (oneDriveAccessToken && isOneDriveConnected) {
			setParameterValues((prev) => {
				const updated = { ...prev };

				// Find TOKEN parameter (case-insensitive)
				const tokenKey = Object.keys(updated).find((key) => key.toLowerCase() === 'token');
				if (tokenKey) {
					updated[tokenKey] = oneDriveAccessToken;
					console.log('[CreatePartner] Auto-filled OneDrive TOKEN parameter');
				}

				// Find REFRESH_TOKEN parameter (case-insensitive)
				if (oneDriveRefreshToken) {
					const refreshKey = Object.keys(updated).find(
						(key) =>
							key.toLowerCase() === 'refresh_token' ||
							key.toLowerCase() === 'encrypted_refresh_token'
					);
					if (refreshKey) {
						updated[refreshKey] = oneDriveRefreshToken;
						console.log('[CreatePartner] Auto-filled OneDrive REFRESH_TOKEN parameter');
					}
				}

				return updated;
			});
		}
	}, [oneDriveAccessToken, oneDriveRefreshToken, isOneDriveConnected]);

	const initialFormikValues = {
		label: '',
		partnerId: '',
		partnerTypeId: '',
		active: true,
	};

	const validationSchema = Yup.object().shape({
		label: Yup.string().required('Partner name is required'),
		partnerId: Yup.string().required('Partner is required'),
		partnerTypeId: Yup.string().required('Partner type is required'),
		active: Yup.boolean(),
	});

	const formik = useFormik({
		initialValues: initialFormikValues,
		validationSchema,
		onSubmit: async (values, { setSubmitting }) => {
			try {
				setApiError(null);
				setApiSuccess(null);

				const selectedPartner = commonPartners.find((p) => p.id === Number(values.partnerId));
				const selectedPartnerType = partnerTypes.find(
					(pt) => pt.id === Number(values.partnerTypeId)
				);

				if (!selectedPartner) {
					setApiError('Please select a partner');
					return;
				}

				if (!selectedPartnerType) {
					setApiError('Please select a partner type');
					return;
				}

				// Check if Google Drive is selected but not connected
				const isGoogleDrive =
					selectedPartner.label?.toLowerCase().includes('google drive') ||
					selectedPartnerType.label?.toLowerCase().includes('google drive');

				if (isGoogleDrive && !isGoogleConnected) {
					setApiError('Please connect to Google Drive before saving');
					return;
				}

				// Check if OneDrive is selected but not connected
				const isOneDrive =
					selectedPartner.label?.toLowerCase().includes('onedrive') ||
					selectedPartner.label?.toLowerCase().includes('one drive') ||
					selectedPartnerType.label?.toLowerCase().includes('onedrive') ||
					selectedPartnerType.label?.toLowerCase().includes('one drive');

				if (isOneDrive && !isOneDriveConnected) {
					setApiError('Please connect to Microsoft OneDrive before saving');
					return;
				}

				// Prepare the payload for creating a new partner_list
				// Build parameters object with user-entered values (including OAuth values if applicable)
				// The parameterValues already contains TOKEN and SHARED_DRIVE_ID if they were defined
				// in the partner's parameters and filled by the useEffect hook
				const finalParameters =
					Object.keys(parameterValues).length > 0
						? parameterValues
						: Array.isArray(selectedPartner.parameters) && selectedPartner.parameters.length > 0
							? selectedPartner.parameters[0]
							: {};

				console.log('[CreatePartner] Is Google Drive:', isGoogleDrive);
				console.log('[CreatePartner] Access Token:', accessToken ? 'present' : 'missing');
				console.log('[CreatePartner] Selected Drive ID:', selectedDriveId);
				console.log('[CreatePartner] Final Parameters:', finalParameters);

				const payload = {
					label: values.label,
					partnerId: selectedPartner.id,
					type: selectedPartnerType['@id'],
					parameters: finalParameters,
					active: values.active,
					deleted: false,
				};

				const createdPartner = await createPartner(payload);

				// Create workplace associations for selected workplaces
				const selectedWorkplaces = workplaces.filter((w) => w.selected);
				if (selectedWorkplaces.length > 0 && createdPartner['@id']) {
					await Promise.all(
						selectedWorkplaces.map((workplace) =>
							createPartnerListWorkplace({
								partnerList: createdPartner['@id'] || '',
								workplace: workplace['@id'] || `/api/workplaces/${workplace.id}`,
							})
						)
					);
				}

				setApiSuccess('Partner created successfully!');

				onUserCreated?.();

				setTimeout(() => {
					backButtonClick();
				}, 1500);
			} catch (error: any) {
				setApiError(error.message || 'Failed to create partner');
			} finally {
				setSubmitting(false);
			}
		},
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		formik.handleSubmit();
	};

	const handleActivateToggle = () => {
		if (!formik.values.active) {
			setActivateDialogOpen(true);
		} else {
			setDeactivateDialogOpen(true);
		}
	};

	const confirmActivatePartner = () => {
		formik.setFieldValue('active', true);
		setActivateDialogOpen(false);
		setWorkplaces((prev) =>
			prev.map((workplace) =>
				workplace.level === 1 ? { ...workplace, selected: true } : workplace
			)
		);
		setApiSuccess('Partner activated successfully! Organization workplace automatically assigned.');
		setTimeout(() => setApiSuccess(null), 3000);
	};

	const confirmDeactivatePartner = () => {
		formik.setFieldValue('active', false);
		setDeactivateDialogOpen(false);
		setWorkplaces((prev) => prev.map((workplace) => ({ ...workplace, selected: false })));
		setApiSuccess('Partner deactivated successfully! All workplace assignments removed.');
		setTimeout(() => setApiSuccess(null), 3000);
	};

	const handleWorkplaceToggle = (workplaceId: number) => {
		const workplace = workplaces.find((w) => w.id === workplaceId);
		if (workplace && workplace.selected) {
			setPendingWorkplaceId(workplaceId);
			setWorkplaceUnassignDialogOpen(true);
		} else {
			toggleWorkplaceAssignment(workplaceId);
		}
	};

	const toggleWorkplaceAssignment = (workplaceId: number) => {
		setWorkplaces((prev) =>
			prev.map((workplace) =>
				workplace.id === workplaceId ? { ...workplace, selected: !workplace.selected } : workplace
			)
		);
	};

	const handleSelectAllWorkplaces = (checked: boolean) => {
		setWorkplaces((prev) => prev.map((workplace) => ({ ...workplace, selected: checked })));
	};

	const allWorkplacesSelected = workplaces.length > 0 && workplaces.every((w) => w.selected);
	const someWorkplacesSelected = workplaces.some((w) => w.selected) && !allWorkplacesSelected;

	// Helper to check if Google Drive is selected
	const isGoogleDriveSelected = () => {
		const selectedPartner = commonPartners.find((p) => p.id === Number(formik.values.partnerId));
		const selectedPartnerType = partnerTypes.find(
			(pt) => pt.id === Number(formik.values.partnerTypeId)
		);

		return (
			selectedPartner?.label?.toLowerCase().includes('google drive') ||
			selectedPartnerType?.label?.toLowerCase().includes('google drive') ||
			false
		);
	};

	// Helper to check if OneDrive is selected
	const isOneDriveSelected = () => {
		const selectedPartner = commonPartners.find((p) => p.id === Number(formik.values.partnerId));
		const selectedPartnerType = partnerTypes.find(
			(pt) => pt.id === Number(formik.values.partnerTypeId)
		);

		return (
			selectedPartner?.label?.toLowerCase().includes('onedrive') ||
			selectedPartner?.label?.toLowerCase().includes('one drive') ||
			selectedPartnerType?.label?.toLowerCase().includes('onedrive') ||
			selectedPartnerType?.label?.toLowerCase().includes('one drive') ||
			false
		);
	};

	const confirmWorkplaceUnassign = () => {
		if (pendingWorkplaceId) {
			toggleWorkplaceAssignment(pendingWorkplaceId);
			const workplace = workplaces.find((w) => w.id === pendingWorkplaceId);
			setApiSuccess(`${workplace?.name} unassigned successfully!`);
			setTimeout(() => setApiSuccess(null), 2000);
		}
		setWorkplaceUnassignDialogOpen(false);
		setPendingWorkplaceId(null);
	};

	return (
		<div className={styles.createPartnerSetting}>
			<Grid container direction="column" spacing={2} pl={2}>
				<Grid container direction="row" justifyContent="space-between">
					<Grid>
						<Button className={styles.backButton} onClick={backButtonClick}>
							Back
						</Button>
					</Grid>
					<Grid item xs={6} display={'flex'} justifyContent="flex-end">
						<Grid>
							<Button
								className={styles.saveButton}
								onClick={handleSubmit}
								disabled={formik.isSubmitting}
							>
								{formik.isSubmitting ? 'Saving...' : 'Save'}
							</Button>
						</Grid>
					</Grid>
				</Grid>
				<Grid xs={12} container direction="row" justifyContent="flex-start" alignItems="center">
					<h2 className={styles.formTitle}>Create New Partner</h2>
				</Grid>
			</Grid>

			{/* API Feedback */}
			{apiSuccess && <div className={styles.successMessage}>{apiSuccess}</div>}
			{apiError && <div className={styles.errorMessage}>{apiError}</div>}

			<form className={styles.formContainer} onSubmit={formik.handleSubmit}>
				{/* Partner Selection */}
				<Autocomplete
					options={commonPartners}
					getOptionLabel={(option) => option.label}
					loading={loadingCommonPartners}
					value={commonPartners.find((p) => p.id === Number(formik.values.partnerId)) || null}
					onChange={(_, newValue) => {
						formik.setFieldValue('partnerId', newValue?.id || '');
						// Auto-fill label with the selected partner's label
						formik.setFieldValue('label', newValue?.label || '');

						// Reset Google OAuth when changing partners
						resetGoogleAuth();
						// Reset OneDrive OAuth when changing partners
						resetOneDriveAuth();

						// Initialize parameter values from the selected partner's parameters
						console.log('parameters newValue', newValue);
						if (newValue?.parameters) {
							const initialParams: Record<string, string> = {};

							// Parameters can be an array of objects or a single object
							if (Array.isArray(newValue.parameters) && newValue.parameters.length > 0) {
								// Get the first parameter object and extract its keys
								const paramObj = newValue.parameters[0];
								if (typeof paramObj === 'object' && paramObj !== null) {
									Object.keys(paramObj).forEach((key) => {
										initialParams[key] = '';
									});
								}
							} else if (typeof newValue.parameters === 'object') {
								// If parameters is directly an object
								Object.keys(newValue.parameters).forEach((key) => {
									initialParams[key] = '';
								});
							}

							console.log('initialParams', initialParams);
							setParameterValues(initialParams);
						} else {
							setParameterValues({});
						}
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Partner *"
							margin="normal"
							error={formik.touched.partnerId && Boolean(formik.errors.partnerId)}
							helperText={formik.touched.partnerId && formik.errors.partnerId}
							InputProps={{
								...params.InputProps,
								endAdornment: (
									<>
										{loadingCommonPartners ? <CircularProgress color="inherit" size={20} /> : null}
										{params.InputProps.endAdornment}
									</>
								),
							}}
						/>
					)}
				/>

				{/* Partner Type Autocomplete */}
				<Autocomplete
					options={partnerTypes}
					getOptionLabel={(option) => option.label}
					loading={loadingPartnerTypes}
					value={partnerTypes.find((pt) => pt.id === Number(formik.values.partnerTypeId)) || null}
					onChange={(_, newValue) => {
						formik.setFieldValue('partnerTypeId', newValue?.id || '');
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Partner Type *"
							margin="normal"
							error={formik.touched.partnerTypeId && Boolean(formik.errors.partnerTypeId)}
							helperText={formik.touched.partnerTypeId && formik.errors.partnerTypeId}
							InputProps={{
								...params.InputProps,
								endAdornment: (
									<>
										{loadingPartnerTypes ? <CircularProgress color="inherit" size={20} /> : null}
										{params.InputProps.endAdornment}
									</>
								),
							}}
						/>
					)}
				/>

				{/* Dynamic Parameter Fields */}
				{Object.keys(parameterValues).length > 0 && (
					<Box
						sx={{
							marginY: 2,
							padding: 2,
							border: '1px solid rgba(0, 0, 0, 0.12)',
							borderRadius: 1,
						}}
					>
						<Box
							sx={{
								fontWeight: 400,
								fontSize: '16px',
								color: 'rgb(0 0 0 / 87%)',
								marginBottom: 2,
							}}
						>
							Partner Configuration Parameters
						</Box>
						{Object.keys(parameterValues).map((key) => {
							const keyLower = key.toLowerCase();
							const isGoogleTokenField =
								isGoogleConnected && (keyLower === 'token' || keyLower === 'access_token');
							const isGoogleDriveIdField =
								isGoogleConnected && (keyLower === 'shared_drive_id' || keyLower === 'drive_id');
							const isReadOnly = isGoogleTokenField || isGoogleDriveIdField;

							return (
								<TextField
									key={key}
									label={key}
									value={parameterValues[key]}
									onChange={(e) => {
										if (isReadOnly) return;
										setParameterValues((prev) => ({
											...prev,
											[key]: e.target.value,
										}));
									}}
									margin="normal"
									fullWidth
									InputProps={{ readOnly: isReadOnly }}
									helperText={isReadOnly ? 'Auto-filled by Google Drive authentication' : undefined}
								/>
							);
						})}
					</Box>
				)}

				{/* Google Drive OAuth Integration */}
				{isGoogleDriveSelected() && (
					<Box
						sx={{
							marginY: 2,
							padding: 2,
							border: '1px solid rgba(0, 0, 0, 0.12)',
							borderRadius: 1,
							backgroundColor: isGoogleConnected
								? 'rgba(76, 175, 80, 0.05)'
								: 'rgba(0, 0, 0, 0.02)',
						}}
					>
						<Box
							sx={{
								fontWeight: 400,
								fontSize: '16px',
								color: 'rgb(0 0 0 / 87%)',
								marginBottom: 1,
							}}
						>
							Google Drive Integration
						</Box>

						<Box
							sx={{
								fontSize: '14px',
								color: 'rgba(0, 0, 0, 0.6)',
								marginBottom: 2,
							}}
						>
							Connect your Google Drive account to enable file integration capabilities.
						</Box>

						{/* OAuth Error Message */}
						{googleAuthError && !isGoogleConnected && (
							<Alert severity="error" sx={{ marginBottom: 2 }}>
								<AlertTitle>Authentication Error</AlertTitle>
								{googleAuthError}
							</Alert>
						)}

						{/* Connection Status */}
						{isGoogleConnected && (
							<Alert severity="success" sx={{ marginBottom: 2 }}>
								<AlertTitle>Connected Successfully</AlertTitle>
								Google Drive has been connected to this partner.
							</Alert>
						)}

						{/* Shared Drive Selection */}
						{isGoogleConnected && sharedDrives.length > 0 && (
							<Box sx={{ marginBottom: 2 }}>
								<Autocomplete
									options={sharedDrives}
									getOptionLabel={(option) => option.name}
									value={sharedDrives.find((d) => d.id === selectedDriveId) || null}
									onChange={(_, newValue) => {
										setSelectedDriveId(newValue?.id || null);
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Select Shared Drive"
											margin="normal"
											helperText={
												sharedDrives.length === 1
													? 'Only one shared drive available (auto-selected)'
													: 'Select a Google Shared Drive to use with this partner'
											}
										/>
									)}
								/>
							</Box>
						)}

						{/* Drive Fetch Error */}
						{isGoogleConnected && googleAuthError && (
							<Alert severity="warning" sx={{ marginBottom: 2 }}>
								<AlertTitle>Shared Drives Access Limited</AlertTitle>
								{googleAuthError}
							</Alert>
						)}

						{/* Connect/Reconnect Button */}
						<Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
							<Button
								onClick={initiateGoogleAuth}
								disabled={isGoogleAuthLoading}
								className={styles.googleDriveButton}
								style={{
									backgroundColor: isGoogleConnected ? '#4caf50' : '#446388',
									color: '#fff',
									padding: '10px 20px',
									fontSize: '14px',
									opacity: isGoogleAuthLoading ? 0.6 : 1,
								}}
							>
								{isGoogleAuthLoading ? (
									<>
										<CircularProgress size={16} sx={{ marginRight: 1, color: 'inherit' }} />
										Authenticating...
									</>
								) : isGoogleConnected ? (
									<>✓ Connected to Google Drive</>
								) : (
									'Connect Google Drive'
								)}
							</Button>

							{/* Reconnect Option */}
							{isGoogleConnected && (
								<Button
									onClick={() => {
										resetGoogleAuth();
										setApiSuccess(null);
										setApiError(null);
									}}
									className={styles.reconnectButton}
									style={{
										color: '#446388',
										border: '1px solid #446388',
										backgroundColor: 'transparent',
										padding: '8px 16px',
										fontSize: '14px',
									}}
								>
									Reconnect
								</Button>
							)}
						</Box>

						{/* Helper Text */}
						{!isGoogleConnected && (
							<Box
								sx={{
									fontSize: '12px',
									color: 'rgba(0, 0, 0, 0.6)',
									marginTop: 1,
								}}
							>
								Note: You must connect Google Drive before you can save this partner.
							</Box>
						)}
					</Box>
				)}

				{/* OneDrive OAuth Integration */}
				{isOneDriveSelected() && (
					<Box
						sx={{
							marginY: 2,
							padding: 2,
							border: '1px solid rgba(0, 0, 0, 0.12)',
							borderRadius: 1,
							backgroundColor: isOneDriveConnected
								? 'rgba(76, 175, 80, 0.05)'
								: 'rgba(0, 0, 0, 0.02)',
						}}
					>
						<Box
							sx={{
								fontWeight: 400,
								fontSize: '16px',
								color: 'rgb(0 0 0 / 87%)',
								marginBottom: 1,
							}}
						>
							Microsoft OneDrive Integration
						</Box>

						<Box
							sx={{
								fontSize: '14px',
								color: 'rgba(0, 0, 0, 0.6)',
								marginBottom: 2,
							}}
						>
							Connect your Microsoft OneDrive account to enable file integration capabilities.
						</Box>

						{/* OAuth Error Message */}
						{oneDriveAuthError && !isOneDriveConnected && (
							<Alert severity="error" sx={{ marginBottom: 2 }}>
								<AlertTitle>Authentication Error</AlertTitle>
								{oneDriveAuthError}
							</Alert>
						)}

						{/* Connection Status */}
						{isOneDriveConnected && (
							<Alert severity="success" sx={{ marginBottom: 2 }}>
								<AlertTitle>Connected Successfully</AlertTitle>
								Microsoft OneDrive has been connected to this partner.
							</Alert>
						)}

						{/* Connect / Reconnect Button */}
						<Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
							<Button
								onClick={initiateOneDriveAuth}
								disabled={isOneDriveAuthLoading}
								className={styles.googleDriveButton}
								style={{
									backgroundColor: isOneDriveConnected ? '#4caf50' : '#446388',
									color: '#fff',
									padding: '10px 20px',
									fontSize: '14px',
									opacity: isOneDriveAuthLoading ? 0.6 : 1,
								}}
							>
								{isOneDriveAuthLoading ? (
									<>
										<CircularProgress size={16} sx={{ marginRight: 1, color: 'inherit' }} />
										Authenticating...
									</>
								) : isOneDriveConnected ? (
									<>&#10003; Connected to Microsoft OneDrive</>
								) : (
									'Connect OneDrive'
								)}
							</Button>

							{/* Reconnect Option */}
							{isOneDriveConnected && (
								<Button
									onClick={() => {
										resetOneDriveAuth();
										setApiSuccess(null);
										setApiError(null);
									}}
									className={styles.reconnectButton}
									style={{
										color: '#446388',
										border: '1px solid #446388',
										backgroundColor: 'transparent',
										padding: '8px 16px',
										fontSize: '14px',
									}}
								>
									Reconnect
								</Button>
							)}
						</Box>

						{/* Helper Text */}
						{!isOneDriveConnected && (
							<Box
								sx={{
									fontSize: '12px',
									color: 'rgba(0, 0, 0, 0.6)',
									marginTop: 1,
								}}
							>
								Note: You must connect Microsoft OneDrive before you can save this partner.
							</Box>
						)}
					</Box>
				)}

				{/* Partner Status */}
				<Box
					sx={{
						marginY: 2,
					}}
				>
					<FormControlLabel
						control={
							<Checkbox
								checked={formik.values.active}
								onChange={handleActivateToggle}
								sx={{
									color: '#446388',
									'&.Mui-checked': {
										color: '#446388',
									},
								}}
							/>
						}
						label={'Active Partner'}
					/>
				</Box>

				{/* Workplace Assignment - Only show if partner is active */}
				{formik.values.active && (
					<Box
						sx={{
							border: '1px solid #ccc',
							borderRadius: 2,
							padding: 2,
							marginY: 2,
						}}
					>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								marginBottom: 2,
							}}
						>
							<Box
								sx={{
									fontWeight: 400,
									fontSize: '16px',
									color: 'rgb(0 0 0 / 87%)',
								}}
							>
								Workplace Assignment
							</Box>
							{!loadingWorkplaces && workplaces.length > 0 && (
								<FormControlLabel
									control={
										<Checkbox
											checked={allWorkplacesSelected}
											indeterminate={someWorkplacesSelected}
											onChange={(e) => handleSelectAllWorkplaces(e.target.checked)}
											sx={{
												color: '#446388',
												'&.Mui-checked': {
													color: '#446388',
												},
												'&.MuiCheckbox-indeterminate': {
													color: '#446388',
												},
											}}
										/>
									}
									label="Select All"
								/>
							)}
						</Box>
						{loadingWorkplaces ? (
							<Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
								<CircularProgress size={24} color="inherit" />
							</Box>
						) : (
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
								{workplaces.map((workplace) => (
									<Box
										key={workplace.id}
										sx={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											padding: '4px 8px',
											borderRadius: 1,
											'&:hover': {
												backgroundColor: 'rgba(0, 0, 0, 0.04)',
											},
										}}
									>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<span style={{ marginLeft: workplace.level * 20 }}>
												{workplace.level > 1 && '└─ '}
												{workplace.name}
											</span>
											{/* {workplace.selected && <Chip label="Assigned" size="small" color="primary" />} */}
										</Box>
										<Checkbox
											checked={workplace.selected}
											onChange={() => handleWorkplaceToggle(workplace.id)}
											sx={{
												color: '#446388',
												'&.Mui-checked': {
													color: '#446388',
												},
											}}
										/>
									</Box>
								))}
							</Box>
						)}
					</Box>
				)}
			</form>

			{/* Partner Activation Confirmation Dialog */}
			<Dialog
				open={activateDialogOpen}
				onClose={() => setActivateDialogOpen(false)}
				aria-labelledby="activate-dialog-title"
				aria-describedby="activate-dialog-description"
			>
				<DialogTitle id="activate-dialog-title">Activate Partner</DialogTitle>
				<DialogContent>
					<DialogContentText id="activate-dialog-description">
						Are you sure you want to activate this partner? This will enable integration
						capabilities.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setActivateDialogOpen(false)} color="primary">
						Cancel
					</Button>
					<Button onClick={confirmActivatePartner} color="primary" autoFocus>
						Activate Partner
					</Button>
				</DialogActions>
			</Dialog>

			{/* Partner Deactivation Confirmation Dialog */}
			<Dialog
				open={deactivateDialogOpen}
				onClose={() => setDeactivateDialogOpen(false)}
				aria-labelledby="deactivate-dialog-title"
				aria-describedby="deactivate-dialog-description"
			>
				<DialogTitle id="deactivate-dialog-title">Deactivate Partner</DialogTitle>
				<DialogContent>
					<DialogContentText id="deactivate-dialog-description">
						Are you sure you want to deactivate this partner? This will disable all integration
						capabilities and remove all workplace assignments.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setDeactivateDialogOpen(false)}
						color="primary"
						className={styles.dialogBackButton}
					>
						Cancel
					</Button>
					<Button
						onClick={confirmDeactivatePartner}
						color="error"
						autoFocus
						className={styles.dialogBackButton}
					>
						Deactivate Partner
					</Button>
				</DialogActions>
			</Dialog>

			{/* Workplace Unassignment Confirmation Dialog */}
			<Dialog
				open={workplaceUnassignDialogOpen}
				onClose={() => {
					setWorkplaceUnassignDialogOpen(false);
					setPendingWorkplaceId(null);
				}}
				aria-labelledby="workplace-unassign-dialog-title"
				aria-describedby="workplace-unassign-dialog-description"
			>
				<DialogTitle id="workplace-unassign-dialog-title">Unassign Workplace</DialogTitle>
				<DialogContent>
					<DialogContentText id="workplace-unassign-dialog-description">
						Are you sure you want to unassign &quot;
						{workplaces.find((w) => w.id === pendingWorkplaceId)?.name}&quot; from this partner?
						This will remove the partner&apos;s access to this workplace.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setWorkplaceUnassignDialogOpen(false);
							setPendingWorkplaceId(null);
						}}
						color="primary"
						className={styles.dialogBackButton}
					>
						Cancel
					</Button>
					<Button
						onClick={confirmWorkplaceUnassign}
						color="warning"
						autoFocus
						className={styles.dialogBackButton}
					>
						Unassign Workplace
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default CreatePartnerSettings;
